import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Pencil, Undo2, History, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const round = (n) => Math.round((parseFloat(n) || 0) * 1000) / 1000;
export const isUnlotted = (mc) => mc?.lot_no === 'UNLOTTED' || mc?.unlotted === true;

/** Amber badge shown on a batch header when lot assignment is pending. */
export function UnlottedBadge({ batch }) {
  const n = batch?.unlotted_rows_pending ?? (batch?.materials_consumed || []).filter(isUnlotted).length;
  if (!n) return null;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded border border-dashed border-amber-500/70 text-amber-300 bg-amber-950/40 whitespace-nowrap"
      data-testid={`unlotted-badge-${batch.id}`}>
      Lot assignment pending ({n})
    </span>
  );
}

/** Picker: choose an existing lot of the same material for an UNLOTTED row. */
export function AssignLotPicker({ batch, row, onClose, onDone }) {
  const [lots, setLots] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/inventory/lots`, { params: { material_id: row.material_id }, withCredentials: true, bypassCache: true })
      .then(r => setLots(r.data || [])).catch(() => setLots([]));
  }, [row.material_id]);
  const assign = async (lot) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/production/batches/${batch.id}/consumption/${row.row_id}/assign-lot`, { lot_id: lot.id }, { withCredentials: true });
      const rem = r.data?.remainder || 0;
      toast.success(rem > 0 ? `Assigned ${r.data.row.quantity_used} to lot ${lot.lot_no}; ${rem} still UNLOTTED` : `Lot ${lot.lot_no} assigned`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not assign lot'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="assign-lot-modal" onClick={onClose}>
      <div className="bg-zinc-900 border border-amber-700/60 rounded-2xl w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="text-zinc-50 font-semibold text-sm">Assign lot · {row.material_name}</h4>
            <p className="text-[11px] text-zinc-500">UNLOTTED {round(row.quantity_used)} kg on batch <span className="font-mono text-sky-300">{batch.id}</span>. Lot remaining goes down; the material total is unchanged.</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        {lots === null ? <div className="text-xs text-zinc-500">Loading lots…</div> : lots.length === 0 ? (
          <div className="text-xs text-amber-300/90 bg-amber-950/30 border border-amber-800/40 rounded-lg px-3 py-2" data-testid="assign-lot-empty">
            No open lot for this material yet. Enter the purchase bill / inward lot first, then come back here.
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto divide-y divide-zinc-800 border border-zinc-800 rounded-lg">
            {lots.map(l => (
              <button key={l.id} disabled={busy} onClick={() => assign(l)} data-testid={`assign-lot-option-${l.id}`}
                className="w-full text-left px-3 py-2 hover:bg-zinc-800/70 disabled:opacity-50 flex items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-xs text-amber-300">{l.lot_no}</div>
                  <div className="text-[10px] text-zinc-500">{l.vendor_name || '—'} · {(l.purchase_date || '').slice(0, 10)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-xs text-zinc-100">{round(l.quantity_remaining)} left</div>
                  {round(l.quantity_remaining) < round(row.quantity_used) && <div className="text-[10px] text-amber-400">partial — remainder stays UNLOTTED</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  lot_consume: 'Consumed from lot', unlotted_consume: 'Consumed UNLOTTED', lot_assign: 'Lot assigned',
  debt_settle: 'Stock debt settled from inward', qty_adjust: 'Quantity adjusted', undo: 'Undo',
};

/** Collapsible stock history of a batch with per-entry Undo (owner/admin). */
export function BatchStockHistory({ batch, isAdmin, onChanged }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState('');
  const load = async () => {
    try {
      const r = await axios.get(`${API}/production/batches/${batch.id}/stock-history`, { withCredentials: true, bypassCache: true });
      setRows(r.data?.movements || []);
    } catch { setRows([]); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, batch.id, batch.materials_consumed?.length]);
  const undo = async (m) => {
    const what = m.type === 'lot_assign'
      ? `put ${round(m.qty)} kg back on lot ${m.lot_no} and mark the row UNLOTTED again`
      : m.type === 'debt_settle'
        ? `return ${round(m.qty)} kg to lot ${m.lot_no} and total, and re-open the stock debt`
        : m.type === 'qty_adjust'
          ? `restore the quantity from ${round(m.new_qty)} back to ${round(m.old_qty)} kg`
          : `restore ${round(m.qty)} kg to ${m.lot_id ? `lot ${m.lot_no} and ` : ''}the ${m.material_name} total and remove this row from the batch`;
    if (!window.confirm(`Undo "${TYPE_LABEL[m.type] || m.type}" for ${m.material_name}?\n\nThis will ${what}.`)) return;
    setBusy(m.id);
    try {
      await axios.post(`${API}/production/batches/${batch.id}/stock-history/${m.id}/undo`, {}, { withCredentials: true });
      toast.success('Undone — stock restored');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Undo failed'); }
    finally { setBusy(''); }
  };
  return (
    <div className="border border-zinc-800 rounded-md overflow-hidden" data-testid={`stock-history-${batch.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[10px] text-zinc-400 hover:bg-zinc-900/70" data-testid={`stock-history-toggle-${batch.id}`}>
        <History size={10} className="text-sky-400" /> Stock history {rows ? `(${rows.length})` : ''} <span className="ml-auto text-zinc-600">{open ? 'hide' : 'show'}</span>
      </button>
      {open && (
        <div className="divide-y divide-zinc-800/60 bg-zinc-950/40">
          {rows === null && <div className="px-2 py-1.5 text-[11px] text-zinc-500">Loading…</div>}
          {rows?.length === 0 && <div className="px-2 py-1.5 text-[11px] text-zinc-600 italic">No logged movements (batch created before the stock log existed).</div>}
          {rows?.map(m => (
            <div key={m.id} className={`px-2 py-1.5 flex items-center gap-2 text-[11px] ${m.undone_at ? 'opacity-50 line-through' : ''}`} data-testid={`stock-move-${m.id}`}>
              <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${m.type === 'undo' ? 'bg-sky-400' : m.type === 'unlotted_consume' ? 'bg-amber-400' : m.type === 'debt_settle' ? 'bg-red-400' : 'bg-emerald-400'}`} />
              <span className="text-zinc-300 truncate">{TYPE_LABEL[m.type] || m.type}{m.type === 'undo' && m.undo_of_type ? ` · ${TYPE_LABEL[m.undo_of_type] || m.undo_of_type}` : ''} — {m.material_name}</span>
              <span className="font-mono text-zinc-100 whitespace-nowrap">{m.type === 'qty_adjust' ? `${round(m.old_qty)} → ${round(m.new_qty)}` : `${round(Math.abs(m.qty))} kg`}</span>
              {m.lot_no && <span className={`font-mono text-[10px] px-1 rounded border ${m.lot_no === 'UNLOTTED' ? 'border-dashed border-amber-600 text-amber-300' : 'border-zinc-700 text-zinc-400'}`}>{m.lot_no}</span>}
              <span className="text-[10px] text-zinc-600 whitespace-nowrap ml-auto">{(m.created_at || '').slice(0, 16).replace('T', ' ')} · {m.by}</span>
              {isAdmin && !m.undone_at && m.type !== 'undo' && (
                <button onClick={() => undo(m)} disabled={busy === m.id} title="Undo this movement" data-testid={`stock-move-undo-${m.id}`}
                  className="p-1 rounded text-zinc-500 hover:text-amber-300 hover:bg-zinc-800 disabled:opacity-40"><Undo2 size={11} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Lot cell + actions for one consumption row. */
export function ConsumptionRowActions({ batch, mc, isAdmin, onChanged }) {
  const [picker, setPicker] = useState(false);
  const adjust = async () => {
    const raw = window.prompt(`Actual quantity for ${mc.material_name} (${mc.lot_no})?\nCurrent: ${round(mc.quantity_used)} kg — the difference is applied to stock.`, String(round(mc.quantity_used)));
    if (raw === null) return;
    const q = parseFloat(raw);
    if (isNaN(q) || q < 0) { toast.error('Enter a valid quantity'); return; }
    try {
      await axios.patch(`${API}/production/batches/${batch.id}/consumption/${mc.row_id}`, { quantity_used: q }, { withCredentials: true });
      toast.success('Quantity updated');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Adjust failed'); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      {isUnlotted(mc) && mc.row_id && (
        <button onClick={() => setPicker(true)} data-testid={`assign-lot-btn-${batch.id}-${mc.row_id}`} title="Assign the real lot"
          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-600/70 text-amber-200 hover:bg-amber-900/40">
          <Link2 size={10} /> Assign lot
        </button>
      )}
      {isAdmin && mc.row_id && (
        <button onClick={adjust} data-testid={`adjust-qty-btn-${batch.id}-${mc.row_id}`} title="Adjust quantity (difference applied to stock)"
          className="p-0.5 rounded text-zinc-500 hover:text-amber-300 hover:bg-zinc-800"><Pencil size={10} /></button>
      )}
      {picker && <AssignLotPicker batch={batch} row={mc} onClose={() => setPicker(false)} onDone={() => { setPicker(false); onChanged?.(); }} />}
    </span>
  );
}
